import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from './icon';

type IconPosition = 'left' | 'right';

interface TextInputProps {
	placeholder: string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
	value: string;
	type?: string;
	required?: boolean;
	inputStyle?: React.CSSProperties;
	labelStyle?: React.CSSProperties;
	wrapperStyle?: React.CSSProperties;
	onBlur?: () => void;
	autoFocus?: boolean;
	noAutocomplete?: boolean;
	disabled?: boolean;
	customClassNameProp?: string;
	icon?: React.ReactNode;
	iconPosition?: IconPosition;
}

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export default function TextInput(props: TextInputProps) {
	const {
		placeholder,
		onChange,
		onKeyUp,
		value,
		type = "text",
		required = false,
		inputStyle = {},
		onBlur,
		autoFocus = false,
		noAutocomplete = false,
		disabled,
		customClassNameProp,
		icon,
		iconPosition = 'right',
	} = props;
	const [hasValue, setHasValue] = useState(false);
	const [replaceType, setReplaceType] = useState(type);
	const [focused, setFocused] = useState(false);
	const textInput = useRef<HTMLInputElement | null>(null);
	let customClassName;

	const iconWrapperStyle = iconPosition === 'left' ? {left: '16px'} : {right: '16px'};

	function keyUp(event: React.KeyboardEvent<HTMLInputElement>) {
		let targ = event.target as HTMLInputElement;
		setHasValue(!!targ.value);
		onKeyUp?.(event);
	}

	function showPassword() {
		setReplaceType('text');
		setTimeout(() => setReplaceType('password'), 2000);
	}

	function animationStart() {
		setHasValue(true);
	}

	useEffect(() => {
		setTimeout(() => {
			const evt = document.createEvent("HTMLEvents");
			evt.initEvent("change", false, true);
			textInput.current?.dispatchEvent(evt);
		}, 500)

	}, [])

	let style = {...inputStyle};
	if (type === "password" && replaceType === "password" && isSafari) {
		style = { ...style, padding: "0px 8px 0px 8px" };
	}

	if (iconPosition === 'left') {
		style = { ...style, paddingLeft: '40px' };
	}

	if (disabled) {
		// Thought this opacity looked better and more readable than the default opacity for a disabled text input
		style = { ...style, opacity: .7 };
	}

	customClassName = customClassNameProp === undefined ? "text-input-wrapper" : `text-input-wrapper ${customClassNameProp}`; 

	let usedIcon = icon;
	if (type === 'password') {
		usedIcon = <Icon name={ICONS.VIEWERS_EYE} size={22} color={COLORS.GRAY} />
	}

	return (<>
		<div
			className={classNames(customClassName, { entered: value !== '' || hasValue })}
		>
			<label 
				className={classNames(
					'label behind-box',
					{
						'active': focused,
						'entered': value !== '' ,
						'is-double-label': iconPosition === 'left'
					}
				)}
			>{placeholder}</label>
			<div className={classNames('hide-label-wrapper', {'is-double-label': iconPosition === 'left'})}>
				<input
					name={placeholder}
					autoComplete={noAutocomplete ? 'off' : 'on'}
					onAnimationStart={animationStart}
					autoFocus={autoFocus}
					ref={textInput}
					onFocus={() => setFocused(true)}
					onBlur={() => {
						setFocused(false)
						onBlur?.()
					}}
					onKeyUp={keyUp}
					value={value}
					type={replaceType}
					required={required}
					onChange={onChange}
					style={style}
					disabled={disabled}
				/>
				<label
					className={classNames(
						'label in-box',
						{ 
							'active': focused,
							'entered': value !== '',
							'is-double-label': iconPosition === 'left'
						}
					)}
				>{placeholder}</label>
					
				{usedIcon && (
					<span 
						className={`icon-wrapper ${type === 'password' ? 'clickable' : ''}`}
						onClick={() => type === 'password' ? showPassword() : null}
						style={iconWrapperStyle}
					>
						{usedIcon}
					</span>
				)}
			</div>
		</div>
	</>)
}